// Ficha da cliente, onda 4: pacotes e selos, fichas parecidas (juntar) e fotos do trabalho.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, ApiError } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { can, useClient, useMe, useRefresh } from '../lib/data.ts';
import { first, money } from '../lib/format.ts';
import { useWallet } from '../lib/store.ts';
import { Avatar } from '../ui/bits.tsx';
import { Skeleton } from '../ui/State.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';

const br = (d: string) => `${d.slice(8)}/${d.slice(5, 7)}`;
type Similar = { id: string; name: string; phone: string; whatsapp: boolean; visits: number };
type Photo = { id: string; kind: 'antes' | 'depois'; createdAt: string };
export type PhotoConsent = { guardar?: string | null; publicar?: string | null };

function Ring({ v, target }: { v: number; target: number }) {
  const r = 17, c = 2 * Math.PI * r, pct = Math.min(1, v / target);
  return <span className="loy-ring"><svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true"><circle cx="22" cy="22" r={r} fill="none" stroke="var(--hair)" strokeWidth="4" />
    <circle cx="22" cy="22" r={r} fill="none" stroke="var(--rose)" strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - pct * c} transform="rotate(-90 22 22)" /></svg><span>{Math.min(v, target)}/{target}</span></span>;
}

// pacotes ativos e cartela de selos
export function WalletBlock({ clientId }: { clientId: string }) {
  const nav = useNav();
  const me = useMe().data!;
  const w = useWallet(clientId).data;
  if (!w) return null;
  const active = w.packages.filter((p) => p.remaining > 0 && !p.expired);
  const l = w.loyalty;
  if (!active.length && !l.on && !can(me, 'cobrar')) return null;
  return (
    <>
      <p className="gl-title">Pacotes e fidelidade</p>
      {l.on && <div className="loy-card"><Ring v={l.stamps} target={l.target} /><span className="who"><strong>{Math.min(l.stamps, l.target)} de {l.target} selos</strong>
        <small>{l.ready ? `Recompensa liberada · ${l.rewardLabel} · resgate na cobrança` : `${l.target - l.stamps} ${l.target - l.stamps > 1 ? 'atendimentos' : 'atendimento'} até a recompensa`}</small></span><span /></div>}
      <div className="gl">
        {active.map((p) => <div key={p.id} className="gl-row"><span>{p.service}</span><b>restam {p.remaining} de {p.sessions}<small>válido até {br(p.expiresOn)} · sessão vale {money(p.sessionValueCents)}</small></b></div>)}
        {can(me, 'cobrar') && <button className="gl-row gl-btn ac-invite" onClick={() => nav.open({ kind: 'pacote', clientId })}><b>+ Vender pacote</b></button>}
      </div>
    </>
  );
}

// "Parece a mesma pessoa": mesmo celular ou mesmos dois primeiros nomes
export function SimilarBlock({ clientId, name }: { clientId: string; name: string }) {
  const nav = useNav();
  const me = useMe().data!;
  const q = useQuery({ queryKey: ['similar', clientId], queryFn: () => api.get<Similar[]>(`/clients/${clientId}/similar`), enabled: can(me, 'juntar') });
  const list = q.data ?? [];
  if (!list.length) return null;
  return (
    <div className="note jt-note" style={{ marginTop: 12 }}><Icon name="users" /><span>Parece a mesma pessoa: {list.map((x) => x.name).join(', ')}.
      {list.map((x) => <button key={x.id} className="gl-act" onClick={() => nav.open({ kind: 'juntar', clientId, otherId: x.id })}>Juntar com {first(x.name) === first(name) ? x.phone || x.name : x.name}</button>)}</span></div>
  );
}

export function JuntarSheet({ clientId, otherId }: { clientId: string; otherId: string }) {
  const nav = useNav();
  const toast = useToast();
  const refresh = useRefresh();
  const qc = useQueryClient();
  const A = useClient(clientId).data, B = useClient(otherId).data;
  const [keep, setKeep] = useState(clientId);
  const [busy, setBusy] = useState(false);
  if (!A || !B) return <Sheet title="Juntar fichas"><Skeleton kind="sheet" rows={4} /></Sheet>;
  const visits = (c: typeof A) => c.history.filter((h) => h.status === 'completed').length;
  const opt = (c: typeof A) => (
    <button key={c.id} className={'jt-opt' + (keep === c.id ? ' on' : '')} role="radio" aria-checked={keep === c.id} onClick={() => setKeep(c.id)}><Avatar id={c.id} name={c.name} />
      <span><b>{c.name}</b><small>{c.phoneDisplay || 'sem celular'} · {visits(c)} {visits(c) === 1 ? 'visita' : 'visitas'}{c.whatsappConsentAt ? ' · WhatsApp autorizado' : ''}</small></span></button>
  );
  const drop = keep === A.id ? B : A, stay = keep === A.id ? A : B;
  async function go() {
    setBusy(true);
    try {
      await api.post(`/clients/${stay.id}/merge`, { otherId: drop.id });
      await refresh(); await qc.invalidateQueries({ queryKey: ['similar'] }); await qc.invalidateQueries({ queryKey: ['wallet'] });
      toast(`Fichas juntadas: visitas, saldos, pacotes, anamnese e fotos agora estão na ficha de ${first(stay.name)}.`);
      nav.go('cliente', { id: stay.id });
    } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); }
  }
  return (
    <Sheet title="Juntar fichas" sub="Parece a mesma pessoa" actions={<><button className="cta" disabled={busy} onClick={go}>{busy ? 'Juntando…' : 'Juntar as duas fichas'}</button><button className="link-back" onClick={nav.close}>Cancelar</button></>}>
      <p className="m-hint an-intro">Qual nome e celular ficam? O resto é somado.</p>
      <div className="jt-opts" role="radiogroup">{opt(A)}{opt(B)}</div>
      <div className="gl" style={{ marginTop: 12 }}>
        <div className="gl-row"><span>Visitas</span><b>{visits(A) + visits(B)}</b></div>
        {(A.credit || B.credit) ? <div className="gl-row"><span>Crédito</span><b>{money(A.credit + B.credit)}</b></div> : null}
        {(A.debt || B.debt) ? <div className="gl-row"><span>Pendência</span><b>{money(A.debt + B.debt)}</b></div> : null}
        <div className="gl-row"><span>Também vão</span><b>histórico, pacotes, selos, anamnese e fotos</b></div>
      </div>
      <p className="m-hint">O celular de {first(drop.name)} fica como segundo telefone. Juntar fica no histórico de alterações.</p>
    </Sheet>
  );
}

// ---------- fotos ----------
export const usePhotos = (clientId: string) => useQuery({ queryKey: ['photos', clientId], queryFn: () => api.get<Photo[]>(`/clients/${clientId}/photos`) });

export function PhotoGrid({ photos, max }: { photos: Photo[]; max?: number }) {
  return <div className="ph-grid">{photos.slice(0, max ?? photos.length).map((p) => (
    <a key={p.id} className="ph real" href={`/api/photos/${p.id}`} target="_blank" rel="noopener"><img src={`/api/photos/${p.id}`} alt={p.kind === 'antes' ? 'Foto de antes' : 'Foto do resultado'} loading="lazy" />{p.kind === 'antes' && <em>antes</em>}</a>
  ))}</div>;
}

export function PhotosBlock({ clientId, consent }: { clientId: string; consent: PhotoConsent }) {
  const nav = useNav();
  const photos = usePhotos(clientId).data ?? [];
  return (
    <>
      <p className="gl-title">Fotos do trabalho</p>
      {photos.length ? <PhotoGrid photos={photos} max={6} /> : <p className="cp-muted">{consent.guardar ? 'Nenhuma foto ainda. No fim da cobrança, "Fotografar o resultado".' : 'Ela ainda não autorizou guardar fotos.'}</p>}
      <button className="gl-row gl-btn gl-link" onClick={() => nav.open({ kind: 'fotos', clientId })}><span /><b>{consent.guardar ? 'Fotografar ou ver todas' : 'Pedir autorização e fotografar'}</b><Icon name="chevR" /></button>
    </>
  );
}

// a foto sai do celular já menor (até 1600 px, JPEG): sobe rápido e ocupa pouco
async function shrink(file: File): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
  const cv = document.createElement('canvas');
  cv.width = Math.round(bmp.width * k); cv.height = Math.round(bmp.height * k);
  cv.getContext('2d')!.drawImage(bmp, 0, 0, cv.width, cv.height);
  return new Promise((ok, no) => cv.toBlob((b) => (b ? ok(b) : no(new Error('foto'))), 'image/jpeg', 0.85));
}

export function FotosSheet({ clientId, appointmentId }: { clientId: string; appointmentId?: string }) {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const c = useClient(clientId).data;
  const photos = usePhotos(clientId).data ?? [];
  const input = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<'antes' | 'depois'>('depois');
  const [busy, setBusy] = useState(false);
  const [offArm, setOffArm] = useState(false);
  if (!c) return <Sheet title="Fotos do trabalho"><Skeleton kind="sheet" rows={3} /></Sheet>;
  const ok: PhotoConsent = c.photoConsent ?? {};
  const reload = () => Promise.all([qc.invalidateQueries({ queryKey: ['client', clientId] }), qc.invalidateQueries({ queryKey: ['photos', clientId] })]);
  async function consent(guardar: boolean, publicar: boolean) {
    try {
      const r = await api.post<{ removed: number }>(`/clients/${clientId}/photo-consent`, { guardar, publicar });
      await reload(); setOffArm(false);
      if (!guardar && r.removed) toast(`${r.removed} ${r.removed > 1 ? 'fotos apagadas' : 'foto apagada'}, como ela pediu.`);
    } catch (e) { toast(msg(e), 'warn'); }
  }
  async function upload(f: File) {
    setBusy(true);
    try {
      const body = await shrink(f);
      const res = await fetch(`/api/clients/${clientId}/photos?kind=${kind}${appointmentId ? `&appointmentId=${appointmentId}` : ''}`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'image/jpeg' }, body });
      if (!res.ok) { const e = (await res.json().catch(() => null))?.error; throw new ApiError(res.status, e?.code ?? 'ERROR', e?.message ?? 'A foto não foi guardada. Tente de novo.'); }
      await reload(); toast(kind === 'antes' ? 'Foto de antes guardada.' : 'Foto do resultado guardada.');
    } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); if (input.current) input.current.value = ''; }
  }
  const tog = (on: boolean, label: string, sub: string, onClick: () => void, disabled = false) => (
    <button className={'an-consent' + (on ? ' on' : '')} role="checkbox" aria-checked={on} disabled={disabled} onClick={onClick}><i aria-hidden="true">{on ? <Icon name="check" /> : null}</i><span><b>{label}</b><small>{sub}</small></span></button>
  );
  return (
    <Sheet title="Fotos do trabalho" sub={c.name} actions={<button className={'cta' + (photos.length ? '' : ' ghost')} onClick={nav.close}>{photos.length ? 'Pronto' : 'Agora não'}</button>}>
      {tog(!!ok.guardar, `${first(c.name)} autoriza guardar fotos das unhas`, ok.guardar ? `Autorizado em ${new Date(ok.guardar).toLocaleDateString('pt-BR')}. Só a equipe vê. Sem rosto.` : 'Ficam na ficha dela, só para a equipe. Sem rosto.',
        () => (ok.guardar ? (photos.length && !offArm ? setOffArm(true) : consent(false, false)) : consent(true, false)))}
      {offArm && <p className="m-hint warn-hint">Tirar a autorização apaga as {photos.length} fotos dela. Toque de novo para confirmar.</p>}
      {tog(!!ok.publicar, 'E publicar no Instagram e no link', ok.publicar ? `Autorizado em ${new Date(ok.publicar).toLocaleDateString('pt-BR')}.` : 'Opcional. Pode mudar quando quiser.', () => consent(!!ok.guardar, !ok.publicar), !ok.guardar)}
      {ok.guardar && <>
        <div className="chipset soft" style={{ marginTop: 12 }}>{(['depois', 'antes'] as const).map((k) => <button key={k} className={kind === k ? 'on' : ''} aria-pressed={kind === k} onClick={() => setKind(k)}>{k === 'depois' ? 'Resultado' : 'Antes'}</button>)}</div>
        <input ref={input} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} aria-label="Escolher foto" />
        <div className="foto-acts"><button className="cta" disabled={busy} onClick={() => input.current?.click()}><Icon name="camera" />{busy ? 'Guardando…' : kind === 'antes' ? 'Foto de antes' : 'Foto do resultado'}</button></div>
        {photos.length > 0 && <PhotoGrid photos={photos} />}
      </>}
    </Sheet>
  );
}
